import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setLanguage } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { pushSupported, enablePush, disablePush, getExistingSubscription } from "@/lib/push";
import { Star, ShieldCheck, LogOut, LayoutDashboard, Menu, MessageCircle, Search, Home, LogIn, Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { ChatSheet } from "@/components/ChatSheet";
import {
    Wrench, Lightning, Hammer, PaintRoller, Broom, Plant, Truck,
    GraduationCap, Camera, Laptop, Heart, CookingPot,
    Gavel, Calculator, Certificate, Briefcase, ClipboardText, PencilRuler,
    HardHat, Ruler, Translate, PenNib, Code, Megaphone, FirstAid, Brain,
    PawPrint, Umbrella, Barbell, MusicNotes,
    Stethoscope, Syringe, Tooth, Carrot, HandHeart, Baby,
    Engine, Tire, Car, CarProfile, SteeringWheel, Taxi, Package, Airplane, Sailboat, Key, Snowflake, Scissors,
    GridFour, Fire, Bug, Broadcast, Door, Needle, Boot, FrameCorners, Confetti, Dog, FilmSlate, Article, ChartLine, Buildings, Detective, Bank, Eyeglasses, FlowerLotus,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "@/components/ui/sheet";

const ICONS = {
    Wrench, Lightning, Hammer, PaintRoller, Broom, Plant, Truck,
    GraduationCap, Camera, Laptop, Heart, CookingPot,
    Gavel, Calculator, Certificate, Briefcase, ClipboardText, PencilRuler,
    HardHat, Ruler, Translate, PenNib, Code, Megaphone, FirstAid, Brain,
    PawPrint, Umbrella, Barbell, MusicNotes,
    Stethoscope, Syringe, Tooth, Carrot, HandHeart, Baby,
    Engine, Tire, Car, CarProfile, SteeringWheel, Taxi, Package, Airplane, Sailboat, Key, Snowflake, Scissors,
    GridFour, Fire, Bug, Broadcast, Door, Needle, Boot, FrameCorners, Confetti, Dog, FilmSlate, Article, ChartLine, Buildings, Detective, Bank, Eyeglasses, FlowerLotus,
};

export function CategoryIcon({ name, ...props }) {
    const Icon = ICONS[name] || Wrench;
    return <Icon weight="duotone" {...props} />;
}

export function Avatar({ src, name, className = "", fallbackClassName = "" }) {
    if (src) return <img src={src} alt={name || ""} className={`object-cover ${className}`} />;
    return (
        <div className={`flex items-center justify-center bg-ink/5 text-ink/40 font-heading font-black ${className} ${fallbackClassName}`} aria-label={name || ""}>
            {name?.[0]?.toUpperCase() || "?"}
        </div>
    );
}

export function CiapumMark({ className = "h-8 w-8" }) {
    return (
        <svg viewBox="0 0 32 32" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Ciapum">
            <rect width="32" height="32" rx="9" fill="#C25E3B" />
            <path d="M19.7 10.7A6.5 6.5 0 1 0 19.7 21.3" stroke="#ffffff" strokeWidth="3.3" strokeLinecap="round" />
            <circle cx="24" cy="9" r="2.1" fill="#ffffff" />
        </svg>
    );
}

export function Logo({ className = "", light = false, testid = "logo-link" }) {
    return (
        <Link to="/" className={`flex items-center gap-2 ${className}`} data-testid={testid}>
            <CiapumMark className="h-8 w-8" />
            <span className={`font-heading text-2xl font-extrabold tracking-tight ${light ? "text-white" : "text-ink"}`}>
                Ciapum
            </span>
        </Link>
    );
}

export function LanguageSwitcher({ className = "" }) {
    const { i18n } = useTranslation();
    const { user } = useAuth();
    const cur = (i18n.language || "it").startsWith("en") ? "en" : "it";
    const change = async (lng) => {
        if (lng === cur) return;
        setLanguage(lng);
        if (user) { try { await api.put("/auth/language", { lang: lng }); } catch (e) { /* ignore */ } }
    };
    return (
        <div className={`inline-flex items-center rounded-full border border-border overflow-hidden text-xs font-bold ${className}`} data-testid="lang-switcher">
            <button onClick={() => change("it")} className={`px-2.5 py-1 transition-colors ${cur === "it" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`} data-testid="lang-it" aria-label="Italiano">IT</button>
            <button onClick={() => change("en")} className={`px-2.5 py-1 transition-colors ${cur === "en" ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`} data-testid="lang-en" aria-label="English">EN</button>
        </div>
    );
}

export function Stars({ value, count, size = 14 }) {
    const { t } = useTranslation();
    if (value == null)
        return <span className="text-xs text-muted-foreground">{t("Nessuna recensione")}</span>;
    return (
        <span className="inline-flex items-center gap-1" data-testid="stars-rating">
            <Star size={size} className="fill-terracotta text-terracotta" />
            <span className="font-semibold text-ink">{value}</span>
            {count != null && <span className="text-muted-foreground text-sm">({count})</span>}
        </span>
    );
}

export function VerifiedBadge({ small }) {
    const { t } = useTranslation();
    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 font-medium border border-emerald-200 ${
                small ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"
            }`}
            data-testid="verified-badge"
        >
            <ShieldCheck className={small ? "h-3 w-3" : "h-3.5 w-3.5"} />
            {t("Verificato")}
        </span>
    );
}

const STATUS = {
    pending: { label: "In attesa", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    confirmed: { label: "Confermato", cls: "bg-blue-50 text-blue-800 border-blue-200" },
    in_progress: { label: "In corso", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
    completed: { label: "Completato", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    cancelled: { label: "Annullato", cls: "bg-rose-50 text-rose-800 border-rose-200" },
    open: { label: "Aperta", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    assigned: { label: "Assegnata", cls: "bg-blue-50 text-blue-800 border-blue-200" },
};
const PAYMENT = {
    unpaid: { label: "Da pagare", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    deposit_paid: { label: "Acconto versato", cls: "bg-sky-50 text-sky-800 border-sky-200" },
    paid: { label: "Pagato", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    paid_escrow: { label: "In garanzia", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    released: { label: "Pagato", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    failed: { label: "Non riuscito", cls: "bg-rose-50 text-rose-700 border-rose-200" },
    refunded: { label: "Rimborsato", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

export function StatusBadge({ status }) {
    const { t } = useTranslation();
    const s = STATUS[status] || STATUS.pending;
    return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.cls}`} data-testid={`status-${status}`}>{t(s.label)}</span>;
}
export function PaymentBadge({ status }) {
    const { t } = useTranslation();
    const s = PAYMENT[status] || PAYMENT.unpaid;
    return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>{t(s.label)}</span>;
}

export function NotificationToggle({ className = "" }) {
    const { t } = useTranslation();
    const [state, setState] = useState("loading");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            if (!pushSupported()) { if (alive) setState("unsupported"); return; }
            if (Notification.permission === "denied") { if (alive) setState("denied"); return; }
            const sub = await getExistingSubscription();
            if (!alive) return;
            setState(Notification.permission === "granted" && sub ? "granted" : "default");
        })();
        return () => { alive = false; };
    }, []);

    const enable = async () => {
        setBusy(true);
        try { await enablePush(); setState("granted"); toast.success(t("Notifiche attivate!")); }
        catch (e) {
            if (e.message === "denied") { setState("denied"); toast.error(t("Permesso negato dal browser.")); }
            else { console.error("Push enable failed:", e); toast.error(t("Impossibile attivare le notifiche su questo dispositivo.")); }
        } finally { setBusy(false); }
    };
    const disable = async () => {
        setBusy(true);
        try { await disablePush(); setState("default"); toast(t("Notifiche disattivate")); }
        catch (e) { /* ignore */ } finally { setBusy(false); }
    };

    if (state === "loading" || state === "unsupported") return null;

    if (state === "denied") {
        return (
            <span className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-ink/5 text-ink/70 px-3 py-1.5 text-xs font-medium ${className}`} data-testid="notif-blocked" title={t("Le notifiche sono bloccate: riattivale dalle impostazioni del browser per questo sito.")}>
                <BellOff className="h-3.5 w-3.5" /> {t("Notifiche bloccate")}
            </span>
        );
    }
    if (state === "granted") {
        return (
            <Button variant="outline" size="sm" disabled={busy} onClick={disable} className={`rounded-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 ${className}`} data-testid="notif-toggle" title={t("Disattiva notifiche")}>
                <BellRing className="h-4 w-4 mr-1.5" /> {t("Notifiche attive")}
            </Button>
        );
    }
    return (
        <Button variant="outline" size="sm" disabled={busy} onClick={enable} className={`rounded-full border-ink/20 ${className}`} data-testid="notif-toggle">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Bell className="h-4 w-4 mr-1.5" />} {t("Attiva notifiche")}
        </Button>
    );
}

export function DashHeader({ title, subtitle, action }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-ink">{title}</h1>
                {subtitle && <p className="mt-2 text-ink/60">{subtitle}</p>}
            </div>
            {action}
        </div>
    );
}

function TabBtn({ active, icon: Icon, label, onClick, testid }) {
    return (
        <button onClick={onClick} className={`flex flex-col items-center justify-center gap-1 transition-colors ${active ? "text-terracotta" : "text-ink/50 active:text-ink"}`} data-testid={testid}>
            <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
            <span className="text-[10px] font-medium">{label}</span>
        </button>
    );
}

export function MobileTabBar() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [unread, setUnread] = useState(0);
    const isMsg = !!user && user.role !== "admin";
    const { t } = useTranslation();

    const refreshUnread = useCallback(async () => {
        if (!isMsg) return;
        try { const { data } = await api.get("/conversations/unread"); setUnread(data.unread || 0); } catch {}
    }, [isMsg]);

    useEffect(() => {
        if (!isMsg) return;
        refreshUnread();
        const id = setInterval(refreshUnread, 12000);
        const onWs = () => refreshUnread();
        window.addEventListener("fipro:ws", onWs);
        return () => { clearInterval(id); window.removeEventListener("fipro:ws", onWs); };
    }, [isMsg, refreshUnread]);

    if (location.pathname === "/auth") return null;

    const path = location.pathname;
    const dashPath = user?.role === "admin" ? "/admin" : user?.role === "professional" ? "/pro" : "/cliente";
    const searchActive = path.startsWith("/professionist");
    const homeActive = path === "/";
    const dashActive = path.startsWith("/cliente") || path.startsWith("/pro") || path.startsWith("/admin");

    return (
        <>
            <div className="h-[4.75rem] md:hidden" aria-hidden="true" />
            <nav
                className="md:hidden fixed bottom-0 inset-x-0 z-50 border-t border-border bg-white/95 backdrop-blur-xl"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                data-testid="mobile-tabbar"
            >
                <div className="grid grid-cols-3 h-[4.75rem]">
                    {!user && (
                        <>
                            <TabBtn active={homeActive} icon={Home} label={t("Home")} onClick={() => navigate("/")} testid="tab-home" />
                            <TabBtn active={searchActive} icon={Search} label={t("Cerca")} onClick={() => navigate("/professionisti")} testid="tab-search" />
                            <TabBtn active={false} icon={LogIn} label={t("Accedi")} onClick={() => navigate("/auth")} testid="tab-login" />
                        </>
                    )}
                    {user && (
                        <>
                            <TabBtn active={dashActive} icon={LayoutDashboard} label={t("Dashboard")} onClick={() => navigate(dashPath)} testid="tab-dashboard" />
                            <TabBtn active={searchActive} icon={Search} label={t("Cerca")} onClick={() => navigate("/professionisti")} testid="tab-search" />
                            {isMsg ? (
                                <ChatSheet onActivity={refreshUnread}>
                                    <button className="flex flex-col items-center justify-center gap-1 text-ink/50 active:text-ink transition-colors" data-testid="tab-messages">
                                        <span className="relative">
                                            <MessageCircle className="h-5 w-5" />
                                            {unread > 0 && (
                                                <span className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-terracotta text-white text-[9px] font-bold flex items-center justify-center">
                                                    {unread > 9 ? "9+" : unread}
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-[10px] font-medium">{t("Messaggi")}</span>
                                    </button>
                                </ChatSheet>
                            ) : (
                                <TabBtn active={homeActive} icon={Home} label={t("Home")} onClick={() => navigate("/")} testid="tab-home" />
                            )}
                        </>
                    )}
                </div>
            </nav>
        </>
    );
}

export function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [unread, setUnread] = useState(0);
    const { t } = useTranslation();

    const refreshUnread = useCallback(async () => {
        if (!user || user.role === "admin") return;
        try {
            const { data } = await api.get("/conversations/unread");
            setUnread(data.unread || 0);
        } catch {}
    }, [user]);

    useEffect(() => {
        if (!user || user.role === "admin") return;
        refreshUnread();
        const id = setInterval(refreshUnread, 12000);
        const onWs = () => refreshUnread();
        window.addEventListener("fipro:ws", onWs);
        return () => { clearInterval(id); window.removeEventListener("fipro:ws", onWs); };
    }, [user, refreshUnread]);

    const dashPath =
        user?.role === "admin" ? "/admin" : user?.role === "professional" ? "/pro" : "/cliente";

    return (
        <header className="sticky top-0 z-50 border-b border-border bg-white/70 backdrop-blur-xl">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
                <Logo />
                <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-ink/80">
                    <Link to="/professionisti" className="hover:text-terracotta transition-colors" data-testid="nav-browse">
                        {t("Trova un professionista")}
                    </Link>
                    <a href="/#come-funziona" className="hover:text-terracotta transition-colors">{t("Come funziona")}</a>
                    <a href="/#categorie" className="hover:text-terracotta transition-colors">{t("Categorie")}</a>
                </nav>
                <div className="flex items-center gap-1.5">
                    {user && user.role !== "admin" && (
                        <ChatSheet onActivity={refreshUnread}>
                            <Button variant="ghost" size="icon" className="relative rounded-full" data-testid="nav-messages" aria-label={t("Messaggi")}>
                                <MessageCircle className="h-5 w-5 text-ink" />
                                {unread > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-terracotta text-white text-[10px] font-bold flex items-center justify-center" data-testid="unread-badge">
                                        {unread > 9 ? "9+" : unread}
                                    </span>
                                )}
                            </Button>
                        </ChatSheet>
                    )}
                    <div className="hidden md:flex items-center gap-2">
                        <LanguageSwitcher className="mr-1" />
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="gap-2 font-medium" data-testid="user-menu-trigger">
                                        <span className="h-7 w-7 rounded-full bg-ink text-white text-xs flex items-center justify-center font-semibold">
                                            {user.name?.[0]?.toUpperCase()}
                                        </span>
                                        <span className="max-w-28 truncate">{user.name}</span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-52">
                                    <DropdownMenuItem onClick={() => navigate(dashPath)} data-testid="menu-dashboard">
                                        <LayoutDashboard className="h-4 w-4 mr-2" /> {t("La mia dashboard")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                                        <LogOut className="h-4 w-4 mr-2" /> {t("Esci")}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Button variant="ghost" onClick={() => navigate("/auth")} data-testid="nav-login" className="font-medium">
                                    {t("Accedi")}
                                </Button>
                                <Button
                                    onClick={() => navigate("/auth?mode=register")}
                                    className="bg-ink hover:bg-inkhover text-white rounded-full px-5 font-medium transition-transform hover:-translate-y-0.5"
                                    data-testid="nav-register"
                                >
                                    {t("Registrati")}
                                </Button>
                            </>
                        )}
                    </div>
                    <MobileNav user={user} logout={logout} navigate={navigate} dashPath={dashPath} />
                </div>
            </div>
        </header>
    );
}

function MobileNav({ user, logout, navigate, dashPath }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const go = (path) => { setOpen(false); navigate(path); };
    const linkCls = "flex items-center gap-3 w-full text-left rounded-xl px-4 py-3 text-base font-medium text-ink hover:bg-creamalt transition-colors";
    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden rounded-full" data-testid="mobile-menu-trigger" aria-label={t("Apri menu")}>
                    <Menu className="h-6 w-6 text-ink" />
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-4/5 max-w-xs p-0 flex flex-col" data-testid="mobile-menu">
                <SheetHeader className="px-6 py-5 border-b border-border text-left">
                    <SheetTitle className="font-heading text-lg">
                        {user ? (
                            <span className="flex items-center gap-2">
                                <span className="h-9 w-9 rounded-full bg-ink text-white text-sm flex items-center justify-center font-semibold shrink-0">
                                    {user.name?.[0]?.toUpperCase()}
                                </span>
                                <span className="truncate">{user.name}</span>
                            </span>
                        ) : (
                            <span className="flex items-center gap-2"><CiapumMark className="h-6 w-6" /> Ciapum</span>
                        )}
                    </SheetTitle>
                </SheetHeader>
                <nav className="flex-1 overflow-y-auto p-2">
                    <button onClick={() => go("/professionisti")} className={linkCls} data-testid="mobile-nav-browse">
                        {t("Trova un professionista")}
                    </button>
                    <a href="/#come-funziona" onClick={() => setOpen(false)} className={linkCls} data-testid="mobile-nav-how">
                        {t("Come funziona")}
                    </a>
                    <a href="/#categorie" onClick={() => setOpen(false)} className={linkCls} data-testid="mobile-nav-categories">
                        {t("Categorie")}
                    </a>
                    {user && (
                        <button onClick={() => go(dashPath)} className={linkCls} data-testid="mobile-menu-dashboard">
                            <LayoutDashboard className="h-5 w-5" /> {t("La mia dashboard")}
                        </button>
                    )}
                </nav>
                <div className="border-t border-border p-4 space-y-2">
                    <div className="flex justify-center pb-1"><LanguageSwitcher /></div>
                    {user ? (
                        <Button onClick={async () => { setOpen(false); await logout(); navigate("/"); }} variant="outline" className="w-full rounded-full" data-testid="mobile-menu-logout">
                            <LogOut className="h-4 w-4 mr-2" /> {t("Esci")}
                        </Button>
                    ) : (
                        <>
                            <Button onClick={() => go("/auth")} variant="outline" className="w-full rounded-full" data-testid="mobile-nav-login">
                                {t("Accedi")}
                            </Button>
                            <Button onClick={() => go("/auth?mode=register")} className="w-full rounded-full bg-ink hover:bg-inkhover text-white" data-testid="mobile-nav-register">
                                {t("Registrati")}
                            </Button>
                        </>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
